import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Paper,
  TextField,
  Alert,
  Chip
} from '@mui/material';
import { EmojiEvents } from '@mui/icons-material';
import { drawService } from '../services/drawService';
import { Draw } from '../types';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { AppHeader } from '../components/AppHeader';

const STATUS_LABELS: Record<Draw['status'], string> = {
  PENDING: 'Pendente',
  IN_PROGRESS: 'Em progresso',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
};

const EditDraw: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [draw, setDraw] = useState<Draw | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawDate, setDrawDate] = useState<Date | null>(null);
  const [maxWinners, setMaxWinners] = useState(1);
  const [allowMultiplePrizesPerUser, setAllowMultiplePrizesPerUser] = useState(false);
  const [allowMultipleWinnersPerStore, setAllowMultipleWinnersPerStore] = useState(false);
  const [cancelDraw, setCancelDraw] = useState(false);

  useEffect(() => {
    const fetchDraw = async () => {
      try {
        setLoading(true);
        const drawData = await drawService.getDrawById(id!);
        setDraw(drawData);
        setDrawDate(new Date(drawData.drawDate));
        setMaxWinners(drawData.maxWinners);
        setAllowMultiplePrizesPerUser(drawData.allowMultiplePrizesPerUser);
        setAllowMultipleWinnersPerStore(drawData.allowMultipleWinnersPerStore);
        setCancelDraw(drawData.status === 'CANCELLED');
      } catch (error) {
        console.error('Erro ao carregar sorteio:', error);
        setError('Erro ao carregar sorteio. Por favor, tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDraw();
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draw || !drawDate) return;

    try {
      setLoading(true);
      setError(null);

      const payload: Partial<Draw> = {
        drawDate: drawDate.toISOString(),
        maxWinners,
        allowMultiplePrizesPerUser,
        allowMultipleWinnersPerStore,
      };

      if (
        cancelDraw &&
        draw.status !== 'CANCELLED' &&
        draw.status !== 'COMPLETED'
      ) {
        payload.status = 'CANCELLED';
      }

      await drawService.updateDraw(draw.id, payload);
      navigate('/draws');
    } catch (error: any) {
      console.error('Erro ao atualizar sorteio:', error);
      const message =
        error.response?.data?.message ||
        'Erro ao atualizar sorteio. Por favor, tente novamente.';
      setError(Array.isArray(message) ? message.join(', ') : message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !draw) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!draw) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error" variant="h6">{error || 'Sorteio não encontrado'}</Typography>
      </Box>
    );
  }

  const validWinnersCount =
    draw.winners?.filter((w) => w.status !== 'REJECTED').length ?? 0;
  const canEdit = draw.status !== 'COMPLETED' && draw.status !== 'CANCELLED';
  const canCancel = draw.status !== 'COMPLETED' && draw.status !== 'CANCELLED';

  return (
    <>
      <AppHeader 
        title="Editar Sorteio"
        subtitle="Atualize as informações do sorteio"
        icon={<EmojiEvents />}
        backTo="/draws"
      />

      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 2
            }}
          >
            <Typography variant="h5" gutterBottom>
              {draw.campaign?.name}
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Prêmio: {draw.prize?.name}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
            )}

            {!canEdit && (
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                {draw.status === 'CANCELLED'
                  ? 'Este sorteio está cancelado e não pode ser editado. Você pode excluí-lo na lista de sorteios.'
                  : 'Sorteios concluídos não podem ser editados.'}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DateTimePicker
                  label="Data do Sorteio"
                  value={drawDate}
                  onChange={(newValue) => setDrawDate(newValue)}
                  disabled={!canEdit}
                  sx={{ width: '100%', mb: 3 }}
                />
              </LocalizationProvider>

              <TextField
                fullWidth
                type="number"
                label="Quantidade Máxima de Ganhadores"
                value={maxWinners}
                onChange={(e) => setMaxWinners(Math.max(1, parseInt(e.target.value, 10) || 1))}
                disabled={!canEdit}
                inputProps={{ min: validWinnersCount || 1, max: draw.prize?.quantity ?? 100000 }}
                helperText={
                  draw.prize
                    ? `Prêmio tem ${draw.prize.quantity} unidade(s) no total. Já sorteados neste sorteio: ${validWinnersCount}.`
                    : undefined
                }
                sx={{ mb: 3 }}
              />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status atual
                </Typography>
                <Chip
                  label={STATUS_LABELS[draw.status]}
                  color={
                    draw.status === 'CANCELLED'
                      ? 'error'
                      : draw.status === 'COMPLETED'
                        ? 'success'
                        : draw.status === 'IN_PROGRESS'
                          ? 'info'
                          : 'default'
                  }
                />
              </Box>

              {canCancel && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={cancelDraw}
                      onChange={(e) => setCancelDraw(e.target.checked)}
                    />
                  }
                  label="Cancelar este sorteio (remove do sorteio ao vivo)"
                  sx={{ mb: 2, display: 'block' }}
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={allowMultiplePrizesPerUser}
                    onChange={(e) => setAllowMultiplePrizesPerUser(e.target.checked)}
                    disabled={!canEdit}
                  />
                }
                label="Permitir múltiplos prêmios por usuário"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={allowMultipleWinnersPerStore}
                    onChange={(e) => setAllowMultipleWinnersPerStore(e.target.checked)}
                    disabled={!canEdit}
                  />
                }
                label="Permitir múltiplos ganhadores por loja"
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/draws')}
                >
                  Voltar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || !canEdit}
                >
                  Salvar Alterações
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}; 
export default EditDraw;
